import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Pencil,
  Download,
  Calendar,
  CreditCard,
  MapPin,
  ClipboardList,
  Clock,
  Package,
  Truck,
  Save,
  User as UserIcon,
  ExternalLink,
  Search,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { adminService, ShoppingOrder } from "../../services/adminService";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/** One row per order line item (Purchase Orders: 4 products => 4 rows) */
type PurchaseOrderRow = { order: ShoppingOrder; item: any; itemIndex: number };
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const PurchaseOrders = () => {
  const [orders, setOrders] = useState<ShoppingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<ShoppingOrder | null>(
    null
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdatingDelivery, setIsUpdatingDelivery] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    courier_name: "",
    tracking_number: "",
    date_shipped: "",
    payment_received: false,
    date_received: "",
  });

  const fetchOrders = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getShoppingOrders({
          page,
          page_size: pagination.pageSize,
          search: search.trim() || undefined,
          ordering: "-created_at",
        });
        let results = data?.results ?? [];
        if (dateFrom || dateTo) {
          results = results.filter((order: ShoppingOrder) => {
            const created = order.created_at
              ? new Date(order.created_at).toISOString().slice(0, 10)
              : "";
            if (dateFrom && created < dateFrom) {
              return false;
            }
            if (dateTo && created > dateTo) {
              return false;
            }
            return true;
          });
        }
        setOrders(results);
        setPagination(prev => ({
          ...prev,
          count: data?.count ?? 0,
          page,
        }));
      } catch (error: unknown) {
        const axiosErr = error as { response?: { status?: number } };
        if (axiosErr?.response?.status === 404) {
          // Out-of-range page (e.g. page 2 when only 1 page): show empty, reset to page 1
          setOrders([]);
          setPagination(prev => ({ ...prev, page: 1 }));
          return;
        }
        console.error("Error fetching orders:", error);
        showErrorToast(error, "Failed to load purchase orders.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, dateFrom, dateTo]
  );

  useEffect(() => {
    fetchOrders(1, searchTerm);
  }, [fetchOrders, searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOrders(1, searchTerm);
  };

  const handleClearDateFilter = () => {
    setDateFrom("");
    setDateTo("");
    setTimeout(() => fetchOrders(1, searchTerm), 0);
  };

  const handleOpenEdit = (order: ShoppingOrder) => {
    setSelectedOrder(order);
    setDeliveryForm({
      courier_name: order.courier_name ?? "",
      tracking_number: order.tracking_number ?? "",
      date_shipped: order.date_shipped ?? "",
      payment_received: !!order.payment_received,
      date_received: order.date_received ?? "",
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateDelivery = async () => {
    if (!selectedOrder) {
      return;
    }
    try {
      setIsUpdatingDelivery(true);
      const cleanedData = {
        ...deliveryForm,
        date_shipped: deliveryForm.date_shipped || null,
        date_received: deliveryForm.date_received || null,
        courier_name: deliveryForm.courier_name || null,
        tracking_number: deliveryForm.tracking_number || null,
      };
      const updated = await adminService.updateShoppingOrder(
        selectedOrder.id,
        cleanedData
      );
      setSelectedOrder(updated);
      toast.success("Order updated successfully");
      fetchOrders(pagination.page, searchTerm);
    } catch (error: unknown) {
      console.error("Error updating order:", error);
      showErrorToast(error, "Failed to update order");
    } finally {
      setIsUpdatingDelivery(false);
    }
  };

  const handleExport = () => {
    if (flattenedRows.length === 0) {
      toast.error("No entries to export.");
      return;
    }
    const exportData = flattenedRows.map(({ order, item }) => {
      const lineTotal =
        item?.price !== null && item?.quantity !== null
          ? parseFloat(String(item.price)) * (item.quantity ?? 1)
          : null;
      return {
        Date: order.created_at
          ? new Date(order.created_at).toLocaleDateString()
          : "",
        "Order ID": order.order_number,
        Organization: order.shipping_address?.organization ?? "N/A",
        "Product / Item": item?.title ?? "—",
        SKU: item?.sku ?? item?.product_id ?? "—",
        Qty: item?.quantity ?? "—",
        "Unit Price":
          item?.price !== null ? parseFloat(String(item.price)) : "—",
        "Line Total": lineTotal ?? "—",
        "Order Total": order.total,
        "Shipping Cost": order.shipping_cost,
        Donation: order.donation_amount,
        Name:
          order.shipping_full_name ?? order.shipping_address?.fullName ?? "N/A",
        Email: order.shipping_email ?? order.shipping_address?.email ?? "N/A",
        Status: order.status,
        Source: order.source,
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase Orders");
    XLSX.writeFile(
      workbook,
      `Purchase_Orders_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    toast.success("Export complete.");
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize) || 1;

  // Flatten to one row per product: order with 4 products => 4 entries
  const flattenedRows: PurchaseOrderRow[] = orders.flatMap(order => {
    const items = order?.items ?? [];
    if (items.length === 0) {
      return [{ order, item: null, itemIndex: 0 }];
    }
    return items.map((item: any, itemIndex: number) => ({
      order,
      item,
      itemIndex,
    }));
  });

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "processing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "shipped":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="mb-4 text-3xl font-bold text-foreground">
              List of all purchase order
            </h2>
            <p className="mb-4 text-sm text-muted-foreground">
              One row per product (e.g. order with 4 products = 4 entries). Edit
              opens the order to update delivery info.
            </p>
            <div className="flex gap-8 text-sm">
              <div>
                <div className="text-muted-foreground">Total Orders</div>
                <div className="text-xl font-semibold text-green-600">
                  {pagination.count}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">
                  Total Number of Pages
                </div>
                <div className="text-xl font-semibold text-green-600">
                  {totalPages}
                </div>
              </div>
            </div>
          </div>
          <Button
            onClick={handleExport}
            className="bg-[#654321] text-white hover:bg-[#543210]"
          >
            <Download className="mr-2 h-4 w-4" />
            Export Data
          </Button>
        </div>

        {/* Search by Order ID and Name */}
        <form
          onSubmit={handleSearch}
          className="w-full min-w-0 rounded-lg border border-border bg-card p-4"
        >
          <div className="mb-2 text-sm font-medium">
            Search by Order ID or Customer Name
          </div>
          <div className="flex min-w-0 max-w-md flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Order ID or name..."
                className="pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              size="sm"
              className="bg-[#654321] text-white hover:bg-[#543210]"
            >
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm("");
                fetchOrders(1, "");
              }}
            >
              Clear
            </Button>
          </div>
        </form>

        {/* Date Filter */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-2 text-sm font-medium">Date</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              placeholder="From"
              className="max-w-[140px]"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
            <Input
              type="date"
              placeholder="To"
              className="max-w-[140px]"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
            <Button variant="outline" size="sm" onClick={handleClearDateFilter}>
              Clear
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Date
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Order ID
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Organization
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Product / Item
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    SKU
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Qty
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Unit Price
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Line Total
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Order Total
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Shipping
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Donation
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Name
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-6 py-12 text-center text-muted-foreground"
                    >
                      <Loader2 className="mx-auto h-8 w-8 animate-spin" />
                      <p className="mt-2">Loading...</p>
                    </td>
                  </tr>
                ) : flattenedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-6 py-12 text-center text-muted-foreground"
                    >
                      No purchase order entries found.
                    </td>
                  </tr>
                ) : (
                  flattenedRows.map(({ order, item }, rowIdx) => {
                    const lineTotal =
                      item?.price !== null && item?.quantity !== null
                        ? parseFloat(String(item.price)) * (item.quantity ?? 1)
                        : null;
                    const isFirstInGroup =
                      rowIdx === 0 ||
                      flattenedRows[rowIdx - 1].order.id !== order.id;
                    const isLastInGroup =
                      rowIdx === flattenedRows.length - 1 ||
                      flattenedRows[rowIdx + 1].order.id !== order.id;
                    const groupClass = isFirstInGroup
                      ? "border-l-4 border-[#654321]/60 bg-[#654321]/10"
                      : "bg-[#654321]/10";
                    const borderClass = !isLastInGroup ? "border-b-0" : "";
                    return (
                      <tr
                        key={`${order.id}-${item?.sku ?? item?.product_id ?? ""}-${item?.title ?? ""}`}
                        className={`${groupClass} ${borderClass} transition-colors hover:bg-[#654321]/20`}
                      >
                        <td className="whitespace-nowrap px-6 py-3 text-sm">
                          {order.created_at
                            ? new Date(order.created_at).toLocaleDateString()
                            : "—"}
                        </td>
                        <td
                          className="max-w-[160px] truncate px-6 py-3 font-mono text-sm"
                          title={order.order_number}
                        >
                          {order.order_number}
                        </td>
                        <td
                          className="max-w-[140px] truncate px-6 py-3 text-sm"
                          title={order.shipping_address?.organization}
                        >
                          {order.shipping_address?.organization ?? "—"}
                        </td>
                        <td
                          className="max-w-[180px] truncate px-6 py-3 text-sm"
                          title={item?.title}
                        >
                          {item?.title ?? "—"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 font-mono text-sm">
                          {item?.sku ?? item?.product_id ?? "—"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 text-sm">
                          {item?.quantity ?? "—"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 text-sm">
                          {item?.price !== null
                            ? `$${parseFloat(String(item.price)).toFixed(2)}`
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 text-sm font-medium">
                          {lineTotal !== null
                            ? `$${lineTotal.toFixed(2)}`
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 text-sm">
                          ${parseFloat(String(order.total ?? 0)).toFixed(2)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 text-sm">
                          $
                          {parseFloat(String(order.shipping_cost ?? 0)).toFixed(
                            2
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3 text-sm">
                          $
                          {parseFloat(
                            String(order.donation_amount ?? 0)
                          ).toFixed(2)}
                        </td>
                        <td
                          className="max-w-[120px] truncate px-6 py-3 text-sm"
                          title={order.shipping_full_name}
                        >
                          {order.shipping_full_name ??
                            order.shipping_address?.fullName ??
                            "—"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-3">
                          <Button
                            size="sm"
                            className="bg-[#654321] text-white hover:bg-[#543210]"
                            onClick={() => handleOpenEdit(order)}
                          >
                            <Pencil className="mr-1 h-3.5 w-3.5" />
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.count > 0 && (
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="text-sm text-muted-foreground">
              Orders{" "}
              <span className="font-medium text-foreground">
                {(pagination.page - 1) * pagination.pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-foreground">
                {Math.min(
                  pagination.page * pagination.pageSize,
                  pagination.count
                )}
              </span>{" "}
              of{" "}
              <span className="font-medium text-foreground">
                {pagination.count}
              </span>
              {flattenedRows.length > 0 && (
                <span className="ml-2 text-muted-foreground">
                  ({flattenedRows.length} line item
                  {flattenedRows.length !== 1 ? "s" : ""} on this page)
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOrders(1, searchTerm)}
                disabled={pagination.page === 1 || loading}
              >
                « First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOrders(pagination.page - 1, searchTerm)}
                disabled={pagination.page === 1 || loading}
              >
                ‹ Back
              </Button>
              <span className="flex items-center rounded border bg-muted/50 px-4 py-2 text-sm font-medium">
                Page {pagination.page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOrders(pagination.page + 1, searchTerm)}
                disabled={pagination.page >= totalPages || loading}
              >
                Next ›
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOrders(totalPages, searchTerm)}
                disabled={pagination.page >= totalPages || loading}
              >
                Last »
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Order Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl gap-0 overflow-y-auto p-0">
          <DialogHeader className="sticky top-0 z-10 border-b bg-muted/30 p-6 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#654321]/10 text-[#654321]">
                <Package className="h-6 w-6" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-2xl font-bold">
                  Edit Order
                </DialogTitle>
                <DialogDescription className="font-mono text-xs">
                  Order ID: {selectedOrder?.order_number}
                </DialogDescription>
              </div>
              <div className="ml-auto">
                <Badge
                  className={`${getStatusColor(selectedOrder?.status ?? "")} px-3 py-1 text-xs uppercase`}
                >
                  {selectedOrder?.status}
                </Badge>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-8 p-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-1 text-sm font-bold text-[#654321]">
                  <ClipboardList className="h-4 w-4" />
                  General Information
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Order Number
                    </span>
                    <span className="font-mono text-xs">
                      {selectedOrder?.order_number}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Source
                    </span>
                    <span className="capitalize">
                      {selectedOrder?.source?.replace("-", " ")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Created
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {selectedOrder?.created_at &&
                        new Date(selectedOrder.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Updated
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {selectedOrder?.updated_at &&
                        new Date(selectedOrder.updated_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-1 text-sm font-bold text-[#654321]">
                  <CreditCard className="h-4 w-4" />
                  Financial Records
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Subtotal
                    </span>
                    <span className="font-mono">
                      $
                      {parseFloat(String(selectedOrder?.subtotal ?? 0)).toFixed(
                        2
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Shipping
                    </span>
                    <span className="font-mono">
                      $
                      {parseFloat(
                        String(selectedOrder?.shipping_cost ?? 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span className="text-[10px] font-bold uppercase">
                      Donation
                    </span>
                    <span className="font-mono">
                      +$
                      {parseFloat(
                        String(selectedOrder?.donation_amount ?? 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-black text-[#654321]">
                    <span className="text-xs font-bold uppercase">
                      Grand Total
                    </span>
                    <span className="font-mono text-lg">
                      $
                      {parseFloat(String(selectedOrder?.total ?? 0)).toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-2 flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Payment
                    </span>
                    <span className="capitalize">
                      {selectedOrder?.payment_method?.replace("_", " ")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-1 text-sm font-bold text-[#654321]">
                  <MapPin className="h-4 w-4" />
                  Customer & Delivery
                </div>
                <div className="space-y-2 rounded border border-dashed border-border bg-muted/20 p-3 text-sm">
                  <div className="flex items-center gap-2 font-bold">
                    <UserIcon className="h-3 w-3" />
                    {selectedOrder?.shipping_full_name ??
                      selectedOrder?.shipping_address?.fullName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selectedOrder?.shipping_email ??
                      selectedOrder?.shipping_address?.email}
                  </div>
                  {selectedOrder?.shipping_address?.organization && (
                    <div className="text-xs font-medium text-[#654321]">
                      {selectedOrder.shipping_address.organization}
                    </div>
                  )}
                  <div className="text-xs">
                    {selectedOrder?.shipping_address?.addressLine1}
                    {selectedOrder?.shipping_address?.addressLine2 &&
                      `, ${selectedOrder.shipping_address.addressLine2}`}
                    <br />
                    {selectedOrder?.shipping_address?.city},{" "}
                    {selectedOrder?.shipping_address?.state}{" "}
                    {selectedOrder?.shipping_address?.postalCode}
                    <br />
                    {selectedOrder?.shipping_address?.country}
                  </div>
                  <div className="text-[10px]">
                    Phone: {selectedOrder?.shipping_address?.phone ?? "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {(selectedOrder?.payment_proof_url ??
              selectedOrder?.payment_proof_notes) && (
              <div className="space-y-2 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-yellow-800">
                  Payment Verification
                </div>
                <div className="flex flex-col gap-2 md:flex-row">
                  {selectedOrder?.payment_proof_url && (
                    <a
                      href={selectedOrder.payment_proof_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded border bg-white px-3 py-2 text-sm text-blue-600 hover:underline"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Payment Proof
                    </a>
                  )}
                  {selectedOrder?.payment_proof_notes && (
                    <div className="flex-1 rounded border bg-white p-3 text-xs text-yellow-900">
                      {selectedOrder.payment_proof_notes}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delivery Information (editable) */}
            <div className="space-y-4 rounded-lg border-2 border-[#654321]/10 bg-[#654321]/5 p-5">
              <div className="flex items-center gap-2 border-b border-[#654321]/20 pb-2 text-sm font-bold text-[#654321]">
                <Truck className="h-5 w-5" />
                Delivery Information
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase">
                      Courier Name
                    </Label>
                    <Input
                      placeholder="e.g. FedEx, BlueDart"
                      value={deliveryForm.courier_name}
                      onChange={e =>
                        setDeliveryForm({
                          ...deliveryForm,
                          courier_name: e.target.value,
                        })
                      }
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase">
                      Tracking No
                    </Label>
                    <Input
                      placeholder="Tracking ID"
                      value={deliveryForm.tracking_number}
                      onChange={e =>
                        setDeliveryForm({
                          ...deliveryForm,
                          tracking_number: e.target.value,
                        })
                      }
                      className="h-9 font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase">
                      Date Shipped
                    </Label>
                    <Input
                      type="date"
                      value={deliveryForm.date_shipped}
                      onChange={e =>
                        setDeliveryForm({
                          ...deliveryForm,
                          date_shipped: e.target.value,
                        })
                      }
                      className="h-9"
                    />
                  </div>
                  <div className="flex items-center space-x-3 pt-2">
                    <Checkbox
                      id="payment_received_po"
                      checked={deliveryForm.payment_received}
                      onCheckedChange={checked =>
                        setDeliveryForm({
                          ...deliveryForm,
                          payment_received: !!checked,
                        })
                      }
                    />
                    <Label
                      htmlFor="payment_received_po"
                      className="cursor-pointer text-sm font-bold"
                    >
                      Payment Received
                    </Label>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase">
                      Payment Received Date
                    </Label>
                    <Input
                      type="date"
                      value={deliveryForm.date_received}
                      onChange={e =>
                        setDeliveryForm({
                          ...deliveryForm,
                          date_received: e.target.value,
                        })
                      }
                      disabled={!deliveryForm.payment_received}
                      className="h-9 disabled:bg-muted disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-center border-t border-[#654321]/10 pt-4">
                <Button
                  onClick={handleUpdateDelivery}
                  disabled={isUpdatingDelivery}
                  className="rounded-md bg-[#654321] px-10 text-white hover:bg-[#4d3219]"
                >
                  {isUpdatingDelivery ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Delivery Info
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Items table (read-only) */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-1 text-sm font-bold text-[#654321]">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Order Items ({selectedOrder?.items?.length ?? 0})
                </div>
              </div>
              <div className="overflow-hidden rounded-md border bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-[#654321]/5 text-[#654321]">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold uppercase">
                        SKU / Item ID
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-bold uppercase">
                        Product Title
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-bold uppercase">
                        Price
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-bold uppercase">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-bold uppercase">
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedOrder?.items?.map((item: any, idx: number) => (
                      <tr
                        key={item.id ?? `${item.product_id}-${idx}`}
                        className="hover:bg-muted/5"
                      >
                        <td className="px-4 py-3 font-mono text-xs font-bold">
                          {item.sku ?? item.product_id}
                        </td>
                        <td className="px-4 py-3 text-xs">{item.title}</td>
                        <td className="px-4 py-3 text-center font-mono text-xs">
                          ${parseFloat(item.price ?? "0").toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-[#654321]">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-medium">
                          $
                          {(
                            parseFloat(item.price ?? "0") * (item.quantity ?? 1)
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-[#654321]/10 bg-muted/10 font-bold">
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-2 text-right text-[10px] uppercase"
                      >
                        Subtotal
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        $
                        {parseFloat(
                          String(selectedOrder?.subtotal ?? 0)
                        ).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <div className="flex justify-end border-t bg-muted/10 p-6">
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PurchaseOrders;
